/**
 * One place for every unit shown in the panel.
 *
 * Scattered `toFixed(2)` calls are how a dashboard ends up saying "0.03 km" in
 * one corner and "34 m" in another for the same number. These functions choose
 * the unit from the magnitude, so the reader never does mental arithmetic — and
 * they all return a dash for null rather than "NaN" or a confident zero, since
 * "we do not know" and "it is zero" are different facts.
 */

export const DASH = '—';

/** "2h 14m", "14m", "45s". Never "0h 0m 45s". */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return DASH;
  if (seconds < 60) return `${Math.round(seconds)}s`;

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

/** Compact form for a dense row: "2:14" for hours, "14m" below an hour. */
export function formatDurationShort(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return DASH;
  if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))}m`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  return `${hours}:${String(minutes).padStart(2, '0')}`;
}

/** Metres below a kilometre, kilometres above it. */
export function formatDistance(meters: number | null | undefined): string {
  if (meters === null || meters === undefined) return DASH;
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(meters < 10_000 ? 2 : 1)} km`;
}

/** km/h, because nobody reads a fleet dashboard in metres per second. */
export function formatSpeed(metersPerSecond: number | null | undefined): string {
  if (metersPerSecond === null || metersPerSecond === undefined) return DASH;
  return `${Math.round(metersPerSecond * 3.6)} km/h`;
}

/** "12s ago", "4m ago", "3h ago". `never` past a year, which is the sentinel age. */
export function formatAge(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return DASH;
  if (seconds > 86_400 * 365) return 'never';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86_400)}d ago`;
}

/**
 * Clock time in the COMPANY's timezone, not the browser's.
 *
 * An admin in London looking at a Bengaluru shift needs the times the employee
 * experienced. Rendering in local time silently shifts every stop by five and a
 * half hours and makes an early shift look like an overnight one.
 */
export function formatClock(iso: string | null | undefined, timezone?: string): string {
  if (!iso) return DASH;
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    ...(timezone ? { timeZone: timezone } : {}),
  });
}

export function formatDate(iso: string | null | undefined, timezone?: string): string {
  if (!iso) return DASH;
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(timezone ? { timeZone: timezone } : {}),
  });
}

const COMPASS = [
  'N',
  'NNE',
  'NE',
  'ENE',
  'E',
  'ESE',
  'SE',
  'SSE',
  'S',
  'SSW',
  'SW',
  'WSW',
  'W',
  'WNW',
  'NW',
  'NNW',
] as const;

/** Mirrors `compassPoint` in @geotracker/core, for bearings computed client-side. */
export function compassPoint(bearing: number): string {
  const normalized = ((bearing % 360) + 360) % 360;
  return COMPASS[Math.round(normalized / 22.5) % 16] as string;
}

/** "NE · 47°" — the label people read plus the number they occasionally need. */
export function formatBearing(bearing: number | null | undefined): string {
  if (bearing === null || bearing === undefined) return DASH;
  return `${compassPoint(bearing)} · ${Math.round(bearing)}°`;
}

/** Percentage with the sign of the trend, for battery deltas. */
export function formatBattery(level: number | null | undefined, charging = false): string {
  if (level === null || level === undefined) return DASH;
  return `${level}%${charging ? ' ⚡' : ''}`;
}

/**
 * Flag codes as sentences.
 *
 * The API emits machine-readable codes on purpose; a panel that prints
 * `not_capturing` at an operations manager has offloaded the translation onto
 * the person least equipped to do it.
 */
export const FLAG_LABELS: Record<string, { label: string; tone: 'danger' | 'warning' | 'muted' }> = {
  never_reported: { label: 'Never reported', tone: 'muted' },
  stale: { label: 'Position stale', tone: 'warning' },
  offline: { label: 'Device offline', tone: 'warning' },
  not_capturing: { label: 'App alive, no fixes', tone: 'danger' },
  session_stale: { label: 'Session went quiet', tone: 'warning' },
  battery_low: { label: 'Battery low', tone: 'warning' },
  gps_disabled: { label: 'GPS off', tone: 'danger' },
  mock_gps: { label: 'Mock location', tone: 'danger' },
  sync_backlog: { label: 'Sync backlog', tone: 'warning' },
  long_stationary: { label: 'Not moved 30m+', tone: 'muted' },
};

export function flagLabel(code: string): { label: string; tone: 'danger' | 'warning' | 'muted' } {
  return FLAG_LABELS[code] ?? { label: code.replace(/_/g, ' '), tone: 'muted' };
}

/** Coordinates as a fallback label when no address could be resolved. */
export function formatCoordinates(latitude: number, longitude: number): string {
  return `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
}

/**
 * How far behind a fix was: "live", "4m late", "3h 20m late".
 *
 * Deliberately not `formatDuration` with a suffix bolted on. Zero and a few
 * seconds both mean the same operational thing — the map was right — and
 * printing "0s late" invites someone to read a rounding artefact as a fault.
 */
export function formatLag(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return DASH;
  if (seconds < 60) return 'live';
  return `${formatDuration(seconds)} late`;
}
